import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutState } from "../../Context/LayoutContext/LayoutContext";
import { useTranslation } from 'react-i18next';

const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const ChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

/**
 * Resolves a child nav `to` path relative to its parent's resolved `to` path.
 *   "../foo"  → sibling of parent (go up one segment, append foo)
 *   "/foo"    → root-relative (strip leading slash)
 *   "foo"     → child of parent (append to parentTo)
 */
const resolveLinkPath = (childTo, parentTo) => {
  if (childTo.startsWith("../")) {
    const siblingPath = childTo.replace("../", "");
    const parentParts = parentTo.split("/");
    if (parentParts.length > 1) {
      parentParts.pop();
      return `${parentParts.join("/")}/${siblingPath}`;
    }
    return siblingPath;
  }
  if (childTo.startsWith("/")) {
    return childTo.replace(/^\//, "");
  }
  return `${parentTo}/${childTo}`;
};

const getUserRoles = () => {
  try {
    let userobjdata = JSON.parse(localStorage.getItem("user_obj_data"));
    if (!userobjdata) return [];
    if (!userobjdata.tokenParsed) return [];
    if (!userobjdata.tokenParsed.resource_access) return [];
    if (!userobjdata.tokenParsed.resource_access["realm-management"]) return [];
    return userobjdata.tokenParsed.resource_access["realm-management"].roles || [];
  } catch (e) {
    return [];
  }
};

/**
 * Strict path match — ONLY exact match or parent-of match.
 * NO last-segment fuzzy matching (that was causing Users to light up on /roles).
 *
 *   isPathMatch("users", "/users")        → true  ✅
 *   isPathMatch("users", "/roles")        → false ✅
 *   isPathMatch("users", "/users/detail") → true  ✅ (child route)
 */
const isPathMatch = (resolvedPath, pathname) => {
  if (!resolvedPath) return false;
  const norm = (p) => p.replace(/^\//, "").replace(/\/$/, "");
  const clean = norm(resolvedPath);
  const current = norm(pathname);
  if (!clean) return false;
  if (current === clean) return true;
  if (current.startsWith(clean + "/")) return true;
  return false;
};

/**
 * Recursively checks whether any leaf path in the children subtree
 * matches the current pathname.
 * parentResolvedPath must already be resolved (not a raw `to` string).
 */
const isAnyDescendantActive = (children, parentResolvedPath, pathname) => {
  if (!children || children.length === 0) return false;
  return children.some((child) => {
    const resolvedPath = resolveLinkPath(child.to, parentResolvedPath);
    if (isPathMatch(resolvedPath, pathname)) return true;
    if (child.children && child.children.length > 0) {
      return isAnyDescendantActive(child.children, resolvedPath, pathname);
    }
    return false;
  });
};

/* ─────────────────────────── NavItem (leaf link) ─────────────────────────── */

const NavItem = ({ headerToPath, label, icon, collapsed, onClick, isActive }) => {
  const getNavStyles = (routerActive) => {
    const active = routerActive || isActive;
    return active
      ? `nav__link ${!collapsed ? "nav__active" : "nav__active--collapsed"}`
      : "nav__link";
  };

  return (
    <li className="nav__item">
      <NavLink
        to={headerToPath}
        end
        className={({ isActive: routerActive }) => getNavStyles(routerActive)}
        title={collapsed ? label : ""}
        onClick={onClick}
      >
        {icon} {collapsed ? null : label}
      </NavLink>
    </li>
  );
};

/* ──────────────────────────── NavItemHeader ──────────────────────────────── */

const NavItemHeader = (props) => {
  const { t } = useTranslation();
  const { label, icon, to: headerToPath, children } = props.item;
  const location = useLocation();
  const { collapsed } = LayoutState();

  const childActive = isAnyDescendantActive(children, headerToPath, location.pathname);

  const [expanded, setExpand] = useState(() => {
    if (isPathMatch(headerToPath, location.pathname)) return true;
    if (isAnyDescendantActive(children, headerToPath, location.pathname)) return true;
    return false;
  });

  const onExpandChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpand((prev) => !prev);
  };

  const parentNavPath =
    collapsed && children && children.length > 0
      ? resolveLinkPath(children[0].to, headerToPath)
      : headerToPath;

  const handleParentClick = (e) => {
    if (!collapsed && children && children.length > 0) {
      e.preventDefault();
      onExpandChange(e);
    }
  };

  return (
    <>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <NavItem
            headerToPath={parentNavPath}
            label={t(label)}
            icon={icon}
            collapsed={collapsed}
            onClick={handleParentClick}
            isActive={childActive}
          />
        </div>
        {!collapsed && children && children.length > 0 && (
          <span
            onClick={onExpandChange}
            style={{
              position: 'absolute',
              right: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              zIndex: 10,
              padding: '4px',
              borderRadius: '4px',
              minWidth: '20px',
              minHeight: '20px',
            }}
          >
            {/* {expanded ? <ChevronUp /> : <ChevronDown />} */}
          </span>
        )}
      </div>

      {expanded && !collapsed && (
        <div style={{ marginLeft: "2em" }}>
          {children &&
            children.map((item, index) => {
              if (item.allowedRoles) {
                const actualRolesFrmURM = getUserRoles();
                if (!item.allowedRoles.some((r) => actualRolesFrmURM.includes(r))) {
                  return null;
                }
              }

              const key = `${item.label}-${index}`;
              const resolvedChildTo = resolveLinkPath(item.to, props.item.to);

              if (item.children && item.children.length > 0) {
                return (
                  <div key={key}>
                    <NavItemHeader
                      item={{
                        ...item,
                        to: resolvedChildTo,
                      }}
                    />
                  </div>
                );
              }

              return (
                <NavItem
                  key={key}
                  headerToPath={resolvedChildTo}
                  label={t(item.label)}
                  icon={item.icon}
                  collapsed={collapsed}
                />
              );
            })}
        </div>
      )}
    </>
  );
};

export default NavItemHeader;
